import React from "react";
import PropTypes from "prop-types";
import ForecastChart from "../../forecast/ForecastChart";
import DriverWaterfall from "./DriverWaterfall";
import { fmtSmape } from "./format";

// AllocationSourceDetail renders the per-source forecast detail: a header
// strip (source_key · algorithm · sMAPE), the shared ForecastChart band, and
// the shared DriverWaterfall decomposition. It reuses the same widgets as
// SeriesDetail but takes the allocation-source data shape directly.
export default function AllocationSourceDetail({
  sourceKey,
  horizon,
  data,
  isLoading,
}) {
  // Gentle prompt when nothing is selected.
  if (!sourceKey) {
    return (
      <div className="flex flex-col items-center justify-center rounded-card border border-dashed border-border bg-surface-2 py-16 text-center">
        <span className="text-3xl" role="img" aria-label="chart">
          📈
        </span>
        <p className="mt-3 text-sm font-medium text-text-2">Select a source</p>
        <p className="mt-1 text-xs text-text-3">
          Click any row in the table to see the per-source forecast detail.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-card border border-card-bd bg-card py-16">
        <span className="text-sm text-text-3">
          Loading detail for &ldquo;{sourceKey}&rdquo;…
        </span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center rounded-card border border-card-bd bg-card py-16">
        <span className="text-sm text-text-3">
          No data available for &ldquo;{sourceKey}&rdquo;.
        </span>
      </div>
    );
  }

  const { series = [], decomposition } = data;
  const algo = data.algorithm ?? null;
  const smape = data.smape ?? null;

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-card-bd bg-card px-5 py-4">
        <div>
          <div className="text-base font-semibold text-text">{sourceKey}</div>
          <div className="mt-0.5 text-xs text-text-3">
            {algo ? (
              <>
                <span className="font-medium text-text-2">{algo}</span>
                <span className="mx-1.5">·</span>
                <span
                  className={smape == null ? "italic text-text-3" : "tabular-nums"}
                >
                  {fmtSmape(smape)}
                </span>
              </>
            ) : (
              <span className="italic text-text-3">model not yet rated</span>
            )}
          </div>
        </div>
      </div>

      {/* Forecast band chart — reuses the exec ForecastChart with the same series shape */}
      {series.length > 0 && (
        <ForecastChart series={series} horizon={horizon} intervalPct={data?.interval_pct ?? 80} />
      )}

      {/* Driver waterfall */}
      {decomposition && <DriverWaterfall decomposition={decomposition} />}
    </div>
  );
}

AllocationSourceDetail.propTypes = {
  sourceKey: PropTypes.string,
  horizon: PropTypes.number,
  data: PropTypes.shape({
    source_key: PropTypes.string,
    algorithm: PropTypes.string,
    smape: PropTypes.number,
    series: PropTypes.array,
    decomposition: PropTypes.shape({
      volume: PropTypes.number,
      price: PropTypes.number,
      other: PropTypes.number,
    }),
  }),
  isLoading: PropTypes.bool,
};
